/**
 * State Machine Registry view (Atlas 3.0 §L2).
 *
 * Renders all 6 Brisarx state machines from FSM_REGISTRY. The registry is
 * automatically merged with any parsed manifest entries during initialization,
 * making unified FSM metadata available to manifest-consuming views.
 * Each machine gets:
 *   - Metadata strip (tier, source file, compliance refs, sandbox flag)
 *   - Pure SVG state diagram (nodes + directed arcs + labels)
 *   - Invariants panel
 *   - Emitted events list
 *
 * Layout algorithm: states laid out in a horizontal chain in definition
 * order. Terminal states rendered differently. Arrows use cubic Bézier
 * curves; self-loops arc above.
 */
import { useMemo, useState, useEffect } from 'react';
import type { AtlasManifest } from '@atlas-manifest/types';
import {
  FSM_REGISTRY,
  type FSMDefinition,
  type FSMState,
  type FSMTransition,
} from '../state/fsmRegistry.js';
import { mergeStateMachines, fsmRegistryToManifestEntries } from '../state/fsmToManifest.js';
import { TIER_CONFIGS } from '../state/eventTaxonomy.js';
import { ViewHeader } from '../components/ViewHeader.js';
import { TabBar } from '../components/TabBar.js';
import { ObjectHeader } from '../components/ObjectHeader.js';
import { RelationshipsPanel } from '../components/RelationshipsPanel.js';
import { EmptyState } from '../components/EmptyState.js';
import { KIND_META } from '../state/objectKinds.js';
import { navigateToObject, navigateToObjectKind } from '../state/hashRouter.js';
import { useAppStore } from '../state/store.js';
import { WorkflowTrail } from './WorkflowTrail.js';

// ─── Layout constants ─────────────────────────────────────────────────────────

const NODE_W = 110;
const NODE_H = 40;
const NODE_RX = 8;
const GAP_X = 60;
const GAP_Y = 80;
const PAD = 48;

interface NodePos {
  id: string;
  x: number;
  y: number;
}

/**
 * Simple left-to-right layered layout.
 * States that are targets only of terminal transitions go on the bottom row.
 */
function layoutStates(machine: FSMDefinition): NodePos[] {
  const main = machine.states.filter((s) => !s.isFinal);
  const terminals = machine.states.filter((s) => s.isFinal);

  const positions: NodePos[] = [];

  main.forEach((s, i) => {
    positions.push({ id: s.id, x: PAD + i * (NODE_W + GAP_X), y: PAD });
  });

  const termRowY = PAD + NODE_H + GAP_Y;
  terminals.forEach((s, i) => {
    const midMain = (main.length - 1) / 2;
    const midTerm = (terminals.length - 1) / 2;
    const offset = i - midTerm + midMain;
    positions.push({
      id: s.id,
      x: PAD + offset * (NODE_W + GAP_X),
      y: termRowY,
    });
  });

  // Avoid negative x
  const minX = Math.min(...positions.map((p) => p.x));
  if (minX < PAD) {
    const shift = PAD - minX;
    positions.forEach((p) => { p.x += shift; });
  }

  return positions;
}

function svgWidth(positions: NodePos[]): number {
  return Math.max(...positions.map((p) => p.x + NODE_W)) + PAD;
}

function svgHeight(positions: NodePos[]): number {
  return Math.max(...positions.map((p) => p.y + NODE_H)) + PAD;
}

// ─── Arc path builder ─────────────────────────────────────────────────────────

const TIER_ARC_COLORS: Record<number, string> = {
  0: '#EF4444',
  1: '#F59E0B',
  2: '#06B6D4',
  3: '#6B7280',
};

function nodeCx(pos: NodePos): number { return pos.x + NODE_W / 2; }

/** Cubic Bézier arc between two node centres, with edge attachment on right/left sides. */
function arcPath(from: NodePos, to: NodePos, idx: number, total: number): string {
  const x1 = from.x + NODE_W;
  const y1 = from.y + NODE_H / 2;
  const x2 = to.x;
  const y2 = to.y + NODE_H / 2;

  // vertical offset so parallel edges don't overlap
  const spread = total > 1 ? (idx - (total - 1) / 2) * 14 : 0;

  if (from.id === to.id) {
    // Self-loop arcs above the node
    const cx = nodeCx(from);
    const cy = from.y;
    return `M ${cx - 20} ${cy} C ${cx - 20} ${cy - 30}, ${cx + 20} ${cy - 30}, ${cx + 20} ${cy}`;
  }

  const dx = x2 - x1;
  const cp1x = x1 + dx * 0.4;
  const cp2x = x1 + dx * 0.6;
  return `M ${x1} ${y1 + spread} C ${cp1x} ${y1 + spread}, ${cp2x} ${y2 + spread}, ${x2} ${y2 + spread}`;
}

/** Midpoint on the cubic Bézier for label placement. */
function arcMid(
  from: NodePos,
  to: NodePos,
  arcIdx: number,
  arcTotal: number,
  srcIdx: number,
  srcTotal: number,
): { x: number; y: number } {
  if (from.id === to.id) {
    return { x: nodeCx(from), y: from.y - 34 };
  }
  const x1 = from.x + NODE_W;
  const y1 = from.y + NODE_H / 2;
  const x2 = to.x;
  const y2 = to.y + NODE_H / 2;
  if (Math.abs(y1 - y2) < 5) {
    // Same row: lift label above nodes to avoid overlap
    const stagger = arcTotal > 1 ? (arcIdx - (arcTotal - 1) / 2) * 11 : 0;
    return { x: (x1 + x2) / 2, y: from.y - 14 + stagger };
  }
  // Cross-row: anchor at t=0.3 from source so labels from different sources don't collide
  const stagger = srcTotal > 1 ? (srcIdx - (srcTotal - 1) / 2) * 14 : 0;
  return { x: x1 + 0.3 * (x2 - x1), y: y1 + 0.4 * (y2 - y1) - 8 + stagger };
}

// ─── State node ───────────────────────────────────────────────────────────────

function StateNode({
  state,
  pos,
  selected,
  onClick,
}: {
  state: FSMState;
  pos: NodePos;
  selected: boolean;
  onClick: () => void;
}): React.JSX.Element {
  const fill = state.isFinal
    ? selected ? '#4B5563' : '#374151'
    : state.isInitial
      ? selected ? '#1D4ED8' : '#2563EB'
      : selected ? '#065F46' : '#1F2937';

  const stroke = selected ? '#FBBF24' : state.isInitial ? '#60A5FA' : state.isFinal ? '#9CA3AF' : '#374151';

  return (
    <g
      role="button"
      tabIndex={0}
      aria-label={`State: ${state.label}`}
      style={{ cursor: 'pointer' }}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      {state.isFinal && (
        <rect
          x={pos.x - 3}
          y={pos.y - 3}
          width={NODE_W + 6}
          height={NODE_H + 6}
          rx={NODE_RX + 2}
          fill="none"
          stroke="#6B7280"
          strokeWidth={1.5}
        />
      )}
      <rect
        x={pos.x}
        y={pos.y}
        width={NODE_W}
        height={NODE_H}
        rx={NODE_RX}
        fill={fill}
        stroke={stroke}
        strokeWidth={selected ? 2 : 1.5}
      />
      <text
        x={pos.x + NODE_W / 2}
        y={pos.y + NODE_H / 2 + 1}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="#F9FAFB"
        fontSize={11}
        fontWeight={state.isInitial ? '600' : '400'}
      >
        {state.label}
      </text>
    </g>
  );
}

// ─── Transition arc ───────────────────────────────────────────────────────────

function TransitionArc({
  transition,
  fromPos,
  toPos,
  arcIndex,
  arcTotal,
  sourceIndex,
  sourceTotal,
  selected,
  onClick,
}: {
  transition: FSMTransition;
  fromPos: NodePos;
  toPos: NodePos;
  arcIndex: number;
  arcTotal: number;
  sourceIndex: number;
  sourceTotal: number;
  selected: boolean;
  onClick: () => void;
}): React.JSX.Element {
  const color = selected
    ? '#FBBF24'
    : (TIER_ARC_COLORS[transition.safetyTier] ?? '#6B7280');

  const d = arcPath(fromPos, toPos, arcIndex, arcTotal);
  const mid = arcMid(fromPos, toPos, arcIndex, arcTotal, sourceIndex, sourceTotal);
  const markerId = `arrow-${transition.id}`;

  return (
    <g>
      <defs>
        <marker
          id={markerId}
          markerWidth={8}
          markerHeight={8}
          refX={6}
          refY={3}
          orient="auto"
        >
          <path d="M0,0 L0,6 L8,3 z" fill={color} />
        </marker>
      </defs>
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth={selected ? 2 : 1.5}
        strokeDasharray={transition.reversible ? undefined : undefined}
        markerEnd={`url(#${markerId})`}
        style={{ cursor: 'pointer' }}
        onClick={onClick}
      />
      <text
        x={mid.x}
        y={mid.y}
        textAnchor="middle"
        fill={color}
        fontSize={9}
        fontWeight="600"
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={onClick}
      >
        {transition.event}
      </text>
      {transition.guard && (
        <text
          x={mid.x}
          y={mid.y + 11}
          textAnchor="middle"
          fill={color}
          fontSize={8}
          fontStyle="italic"
          opacity={0.8}
        >
          [{transition.guard}]
        </text>
      )}
    </g>
  );
}

// ─── Single machine diagram ───────────────────────────────────────────────────

function MachineDiagram({ machine }: { machine: FSMDefinition }): React.JSX.Element {
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [selectedTransition, setSelectedTransition] = useState<string | null>(null);

  const positions = useMemo(() => layoutStates(machine), [machine]);
  const posMap = useMemo(
    () => new Map(positions.map((p) => [p.id, p])),
    [positions],
  );
  const stateMap = useMemo(
    () => new Map(machine.states.map((s) => [s.id, s])),
    [machine.states],
  );

  const w = svgWidth(positions);
  const h = svgHeight(positions);
  const tierConf = TIER_CONFIGS[machine.safetyTier];

  // Group parallel transitions between same pairs
  const arcGroups = useMemo(() => {
    const groups = new Map<string, FSMTransition[]>();
    for (const t of machine.transitions) {
      const key = `${t.from}→${t.to}`;
      const existing = groups.get(key) ?? [];
      existing.push(t);
      groups.set(key, existing);
    }
    return groups;
  }, [machine.transitions]);

  // Group transitions by source state for cross-row label stagger
  const sourceGroups = useMemo(() => {
    const groups = new Map<string, FSMTransition[]>();
    for (const t of machine.transitions) {
      const existing = groups.get(t.from) ?? [];
      existing.push(t);
      groups.set(t.from, existing);
    }
    return groups;
  }, [machine.transitions]);

  const sel = selectedState ? stateMap.get(selectedState) : null;
  const selTrans = selectedTransition
    ? machine.transitions.find((t) => t.id === selectedTransition)
    : null;

  return (
    <div className="smv-machine">
      <div className="smv-machine-meta">
        <div className="smv-meta-left">
          <span
            className="smv-tier-badge"
            style={{ background: tierConf.color }}
          >
            T{machine.safetyTier} · {tierConf.label}
          </span>
          {machine.sandboxed && (
            <span className="smv-badge smv-badge--sandbox">Sandboxed</span>
          )}
          {machine.readOnly && (
            <span className="smv-badge smv-badge--readonly">Read-Only</span>
          )}
        </div>
        <code className="smv-source">{machine.sourceFile}</code>
      </div>

      {machine.complianceRefs.length > 0 && (
        <div className="smv-compliance-refs">
          {machine.complianceRefs.map((ref) => (
            <span key={ref} className="smv-ref-chip">{ref}</span>
          ))}
        </div>
      )}

      <div className="smv-diagram-wrap">
        <svg
          viewBox={`0 0 ${w} ${h}`}
          width={w}
          height={h}
          className="smv-svg"
          aria-label={`State diagram for ${machine.label}`}
          role="img"
        >
          {/* Render transitions */}
          {machine.transitions.map((t) => {
            const fromPos = posMap.get(t.from);
            const toPos = posMap.get(t.to);
            if (!fromPos || !toPos) return null;
            const key = `${t.from}→${t.to}`;
            const group = arcGroups.get(key) ?? [];
            const arcIdx = group.indexOf(t);
            const srcGroup = sourceGroups.get(t.from) ?? [];
            const srcIdx = srcGroup.indexOf(t);
            return (
              <TransitionArc
                key={t.id}
                transition={t}
                fromPos={fromPos}
                toPos={toPos}
                arcIndex={arcIdx}
                arcTotal={group.length}
                sourceIndex={srcIdx}
                sourceTotal={srcGroup.length}
                selected={selectedTransition === t.id}
                onClick={() =>
                  setSelectedTransition((prev) => (prev === t.id ? null : t.id))
                }
              />
            );
          })}

          {/* Render state nodes on top */}
          {machine.states.map((s) => {
            const pos = posMap.get(s.id);
            if (!pos) return null;
            return (
              <StateNode
                key={s.id}
                state={s}
                pos={pos}
                selected={selectedState === s.id}
                onClick={() =>
                  setSelectedState((prev) => (prev === s.id ? null : s.id))
                }
              />
            );
          })}
        </svg>

        {/* Detail panel for selected item */}
        {(sel || selTrans) && (
          <div className="smv-detail-panel">
            {sel && (
              <>
                <h4>{sel.label}</h4>
                <dl>
                  <dt>Type</dt>
                  <dd>
                    {sel.isInitial ? 'Initial' : ''}
                    {sel.isFinal ? 'Terminal' : ''}
                    {!sel.isInitial && !sel.isFinal ? 'Intermediate' : ''}
                  </dd>
                  {sel.description && (
                    <>
                      <dt>Description</dt>
                      <dd>{sel.description}</dd>
                    </>
                  )}
                </dl>
                {machine.invariants
                  .filter((inv) => inv.applyInStates.includes(sel.id))
                  .map((inv) => (
                    <div key={inv.id} className="smv-inv">
                      <span className="material-symbols-rounded smv-inv-icon">lock</span>
                      {inv.description}
                    </div>
                  ))}
              </>
            )}
            {selTrans && (
              <>
                <h4>{selTrans.event}</h4>
                <dl>
                  <dt>From → To</dt>
                  <dd>
                    <code>{selTrans.from}</code> → <code>{selTrans.to}</code>
                  </dd>
                  <dt>Reversible</dt>
                  <dd>{selTrans.reversible ? 'Yes' : 'No (irreversible)'}</dd>
                  <dt>Safety Tier</dt>
                  <dd>
                    <span
                      className="smv-tier-badge"
                      style={{
                        background: TIER_ARC_COLORS[selTrans.safetyTier] ?? '#6B7280',
                      }}
                    >
                      T{selTrans.safetyTier}
                    </span>
                  </dd>
                  {selTrans.guard && (
                    <>
                      <dt>Guard</dt>
                      <dd><em>{selTrans.guard}</em></dd>
                    </>
                  )}
                  {selTrans.description && (
                    <>
                      <dt>Notes</dt>
                      <dd>{selTrans.description}</dd>
                    </>
                  )}
                </dl>
              </>
            )}
          </div>
        )}
      </div>

      {machine.emitsEvents.length > 0 && (
        <div className="smv-emits">
          <span className="smv-emits-label">Emits:</span>
          {machine.emitsEvents.map((ev) => (
            <code key={ev} className="smv-event-chip">{ev}</code>
          ))}
        </div>
      )}

      {machine.invariants.length > 0 && (
        <details className="smv-invariants-details">
          <summary>{machine.invariants.length} invariant{machine.invariants.length > 1 ? 's' : ''}</summary>
          <ul className="smv-invariant-list">
            {machine.invariants.map((inv) => (
              <li key={inv.id}>
                <strong>[{inv.applyInStates.join(', ')}]</strong> {inv.description}
                {inv.expression && (
                  <code className="smv-expr">{inv.expression}</code>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

// ─── Tab definitions ─────────────────────────────────────────────────────────

type MachineTab = 'checkout' | 'onboarding' | 'cancellation' | 'phoneotp' | 'consent' | 'migration';

const FSM_TABS = [
  { id: 'checkout' as MachineTab, label: 'Checkout', icon: 'shopping_cart' },
  { id: 'onboarding' as MachineTab, label: 'OnboardingV2', icon: 'person_add' },
  { id: 'cancellation' as MachineTab, label: 'Cancellation', icon: 'cancel' },
  { id: 'phoneotp' as MachineTab, label: 'PhoneOTP', icon: 'phone_in_talk' },
  { id: 'consent' as MachineTab, label: 'Consent', icon: 'handshake' },
  { id: 'migration' as MachineTab, label: 'Migration', icon: 'published_with_changes' },
] as const;

const TAB_TO_MACHINE_ID: Record<MachineTab, string> = {
  checkout: 'checkoutMachine',
  onboarding: 'onboardingMachine',
  cancellation: 'cancellationMachine',
  phoneotp: 'phoneOTPMachine',
  consent: 'consentMachine',
  migration: 'migrationWizardMachine',
};

// ─── Root view ────────────────────────────────────────────────────────────────

interface StateMachineViewProps {
  /** The manifest (containing parsed state machine entries) to merge with FSM_REGISTRY. */
  manifest: AtlasManifest;
  /** When set (from `#statemachine/<id>`), renders a per-machine detail block above the registry. */
  machineId?: string;
}

export function StateMachineView({ manifest, machineId }: StateMachineViewProps): React.JSX.Element {
  const [activeTab, setActiveTab] = useState<MachineTab>('checkout');

  // Merge manifest and registry state machines for unified view
  const allMachines = useMemo(
    () =>
      mergeStateMachines(
        manifest?.stateMachines ?? [],
        fsmRegistryToManifestEntries(FSM_REGISTRY)
      ),
    [manifest?.stateMachines]
  );

  const routedMachine = machineId ? allMachines.find((m) => m.id === machineId) : undefined;
  const { entityTrail } = useAppStore();

  // Ensure merged machines are initialized (validates manifest parsing)
  useEffect(() => {
    if (allMachines.length === 0) {
      console.warn('[StateMachineView] No merged state machines found');
    }
  }, [allMachines]);

  // Find the active FSMDefinition from the registry for rendering.
  // The allMachines merged result is available for extended manifest consumers.
  const activeMachine = FSM_REGISTRY.find((m) => m.id === TAB_TO_MACHINE_ID[activeTab]) ?? FSM_REGISTRY[0];

  return (
    <div className="smv-root">
      {machineId && (
        routedMachine ? (
          <article style={{ marginBottom: 'var(--sp-xl)' }}>
            <ObjectHeader
              object={routedMachine}
              icon={KIND_META.statemachine.icon}
              iconLabel={KIND_META.statemachine.label}
              badges={[]}
              title={routedMachine.name}
              docRef={<>{routedMachine.id}</>}
              chips={routedMachine.safetyTier ? [{ label: routedMachine.safetyTier }] : []}
            />
            {entityTrail.length >= 2 && (
              <WorkflowTrail trail={entityTrail} onJump={navigateToObject} />
            )}
            <RelationshipsPanel manifest={manifest} kind="statemachine" id={routedMachine.id} onNavigate={navigateToObjectKind} />
          </article>
        ) : (
          <EmptyState icon="help" title={`No state machine "${machineId}"`} detail="It may have been renamed or removed." />
        )
      )}
      <ViewHeader
        eyebrow="STATE MACHINES"
        title="FSM Registry"
        subtitle={`${FSM_REGISTRY.length} registered state machines · XState v5`}
      />

      <TabBar tabs={FSM_TABS} activeTab={activeTab} onTabChange={setActiveTab} />

      <div className="smv-body">
        <h2 className="smv-machine-title">{activeMachine.label}</h2>
        <p className="smv-machine-desc">{activeMachine.description}</p>
        <MachineDiagram machine={activeMachine} />
      </div>

      {/* Legend */}
      <div className="smv-legend">
        <h3>Arc color = transition safety tier</h3>
        <div className="smv-legend-items">
          {TIER_CONFIGS.map((t) => (
            <div key={t.tier} className="smv-legend-item">
              <svg width={28} height={8} aria-hidden="true">
                <line x1={0} y1={4} x2={28} y2={4} stroke={t.color} strokeWidth={2} />
              </svg>
              <span>T{t.tier} · {t.label}</span>
            </div>
          ))}
        </div>
        <div className="smv-legend-nodes">
          <div className="smv-legend-node smv-legend-node--initial">Initial state</div>
          <div className="smv-legend-node smv-legend-node--terminal">Terminal state (double border)</div>
          <div className="smv-legend-node smv-legend-node--regular">Intermediate state</div>
        </div>
      </div>
    </div>
  );
}
